/**
 * 🔒 What a printed sales line carries beside its figures (T403): a taxable
 * mark on its Amount and its list price under its Unit Price. The product
 * owner, 2026-09-27: *"Place a taxable indicator Under Amount and add a list
 * price somewhere in there since we normally are not more than list"*.
 *
 * 🔒 Both are worked out as the projection is built, from what the line
 * already stores, so no column and no flag is added for either.
 */
import { formatCurrency } from '../money';
import { t } from '../strings';
import { printDrawnColumns, type PrintLine, type PrintLineColumn } from '../documents/printRegions';

/**
 * 🔒 The documents a line prints its list price on (decision 1063): the five
 * sales documents, each line of which seeds from an Item carrying one. A
 * purchase document names the vendor's price, which no list price judges, and
 * every other kind prints no Item price at all.
 */
export const PRINT_LIST_PRICE_DOCUMENT_TYPES = ['Quote', 'SalesOrder', 'Invoice', 'CreditNote', 'ChangeOrder'] as const;

export function printCarriesListPrice(documentType: string): boolean {
  return (PRINT_LIST_PRICE_DOCUMENT_TYPES as readonly string[]).includes(documentType);
}

/**
 * 🔒 The documents whose lines carry the taxable mark (decision 1062): the
 * same five sales documents. 🔨 A purchase document prints none of our tax, so
 * a Purchase Order or a Bill never carries the mark or the legend.
 */
export const PRINT_TAX_MARK_DOCUMENT_TYPES = PRINT_LIST_PRICE_DOCUMENT_TYPES;

export function printCarriesTaxMark(documentType: string): boolean {
  return (PRINT_TAX_MARK_DOCUMENT_TYPES as readonly string[]).includes(documentType);
}

/**
 * 🔒 Whether a line is taxable on its sheet (decision 1062): it was charged a
 * rate above nought, or carries tax. 🔨 It reads the tax the line was charged
 * when the document was saved — the category answered against the document's
 * jurisdiction then — and never a flag of its own, and never today's answer,
 * which would change a sent document's sheet.
 */
export function printLineIsTaxable(taxRatePercent: number | null, taxCents: number | null): boolean {
  return (taxRatePercent ?? 0) > 0 || (taxCents ?? 0) !== 0;
}

/**
 * 🔒 A line's list price as the sheet prints it, or nothing (decision 1063):
 * the Item's `listPrice` where it has one and it differs from the line's own
 * unit price. A line at list prints no second figure, because the one it
 * prints already is the list.
 */
export function printLineListPrice(listPriceCents: number | null, unitPriceCents: number | null, currencyCode: string): string | undefined {
  const shown = shownLineListPriceCents(listPriceCents, unitPriceCents);
  return shown === null ? undefined : formatCurrency({ amount: shown, currencyCode });
}

/**
 * 🔒 The same answer in cents, for the document page's view mode, which draws
 * the figure through its own money component (decision 10040): null wherever
 * the sheet prints no list price.
 */
export function shownLineListPriceCents(listPriceCents: number | null, unitPriceCents: number | null): number | null {
  if (listPriceCents === null || listPriceCents <= 0 || unitPriceCents === null || listPriceCents === unitPriceCents) return null;
  return listPriceCents;
}

/** 🔒 What a sales line's row on the document page carries beside its figures, the sheet's two marks (decision 10040). */
export interface ViewLineMarks {
  /** The sheet's taxable T beside the amount. */
  taxable?: boolean;
  /** The list price beneath the unit price, where the sheet prints one; frozen at the send. */
  listPriceCents?: number | null;
}

/**
 * 🔒 Whether the legend under the table prints (decision 1062): the Amount
 * column is drawn and any line on the document is taxable. It prints on the
 * document's last sheet, beneath the table's end.
 */
export function printTaxLegendShown(lines: readonly PrintLine[], columns: readonly PrintLineColumn[]): boolean {
  return printDrawnColumns(columns).includes('amount') && lines.some((line) => line.taxable === true);
}

/**
 * 🔒 An allowance line's description on the sheet (decision 2612; T1911): the
 * word *Allowance* stands beneath it on a line of its own, so the customer reads
 * the amount as authorized for work billed at actuals. The Item cell and the
 * Description cell keep a typed line break, so the mark draws under the text.
 */
export function printAllowanceDescription(description: string, isAllowance: boolean | null | undefined): string {
  if (isAllowance !== true) return description;
  return description === '' ? t('item.allowance') : `${description}\n${t('item.allowance')}`;
}
